<template>
    <div class="content">
        <primevue-select class="content-item" v-model="selectedFeedbackType" :options="FEEDBACK_TYPE_OPTIONS" placeholder="Select feedback type" />
        <primevue-select class="content-item" v-model="selectedPriority" :options="PRIORITY_OPTIONS" placeholder="Select priority" />
        <p-textarea
            class="content-item"
            :disabled="!selectedFeedbackType"
            auto-resize
            maxlength="500"
            placeholder="Please provide feedback"
            v-model="feedbackText"
            rows="5"
            cols="50"
            label="Feedback"
        />
    </div>
    <div class="footer">
        <p-button v-tooltip.top="isSubmitDisabled ? 'Please complete all above fields' : undefined" :disabled="isSubmitDisabled" @click="submitFeedback">
            Submit
        </p-button>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import PrimevueSelect from 'primevue/select';
import PButton from 'primevue/button';
import PTextarea from 'primevue/textarea';
import { feedbackApi } from '@/api/powerMarkets/feedbackApi.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useToast } from 'primevue/usetoast';

enum FeedbackType {
    Issue = 'Issue',
    FeatureRequest = 'Feature Request',
    Other = 'Other',
}

enum Priority {
    Low = 'Low',
    Medium = 'Medium',
    High = 'High',
}

const FEEDBACK_TYPE_OPTIONS = Object.values(FeedbackType);
const PRIORITY_OPTIONS = Object.values(Priority);

const route = useRoute();
const userSettingsStore = useUserSettingsStore();
const toast = useToast();

const emit = defineEmits<{ 'close:overlay': [] }>();

const feedbackText = ref<string>('');
const selectedFeedbackType = ref<FeedbackType | null>(null);
const selectedPriority = ref<Priority | null>(null);

const userId = computed<string>(() => userSettingsStore.userSettings.otherSettings.data.userId);
const userEmail = computed<string>(() => userSettingsStore.userSettings.otherSettings.data.userEmail);
const pageName = computed<string>(() => route.meta.headName);
const isSubmitDisabled = computed<boolean>(() => !feedbackText.value || !selectedFeedbackType.value || !selectedPriority.value);

function submitFeedback (): void {
    if (isSubmitDisabled.value) {
        toast.add({
            severity: 'warn',
            summary: 'Submission Error',
            detail: 'Please complete all fields before submitting.',
        });

        return;
    }

    feedbackApi.postFeedback({
        type: selectedFeedbackType.value!,
        notes: feedbackText.value,
        userId: userId.value,
        email: userEmail.value,
        page: pageName.value,
        priority: selectedPriority.value!.toLowerCase(),
        isFromSpark: true,
    });

    toast.add({
        severity: 'success',
        summary: 'Feedback submitted',
        detail: 'Thank you for your feedback!',
    });

    emit('close:overlay');
}

watch(() => route.path, () => {
    emit('close:overlay');
});

</script>

<style lang="scss" scoped>
.content {
    display: flex;
    flex-direction: column;
    margin: var(--smallSpacing);

    .content-item {
        margin-top: var(--defaultSpacing);
    }
}
.footer {
    display: flex;
    justify-content: flex-end;
    padding: var(--defaultSpacing);
}

</style>
